import React from 'react';
import { colors } from 'src/theme';
import { Flex } from '../../grid';
import { Text } from '../../typography';
import type { GqlField, GqlFollowUp, QuestionnaireAnswers } from './healthQuestionnaire.types';

interface FollowUpFieldsProps {
  followUp: GqlFollowUp;
  answers: QuestionnaireAnswers;
  onChange: (answers: QuestionnaireAnswers) => void;
  /** Render values as static text instead of inputs/selects (used by the print page). */
  displayMode?: boolean;
}

export const FollowUpFields: React.FC<FollowUpFieldsProps> = ({ followUp, answers, onChange, displayMode }) => {
  const fields = followUp.fields ?? [];
  if (fields.length === 0) return null;

  const fieldValues = answers.followUpFields[followUp.code] ?? {};

  const handleFieldChange = (field: GqlField, val: string) => {
    onChange({
      ...answers,
      followUpFields: {
        ...answers.followUpFields,
        [followUp.code]: { ...fieldValues, [field.code]: val },
      },
    });
  };

  return (
    <Flex vertical gap={12} style={{ marginTop: 8 }}>
      {fields.map((field) => {
        if (displayMode) {
          const raw = fieldValues[field.code] ?? '';
          const display =
            field.inputType === 'select' && field.options?.length
              ? field.options.find((opt) => opt.value === raw)?.label ?? raw
              : raw;
          return (
            <div key={field.code}>
              <Text type="small" bold style={{ marginBottom: 4 }}>{field.label}</Text>
              <Text type="base" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {display || '—'}
              </Text>
            </div>
          );
        }

        if (field.inputType === 'select' && field.options?.length) {
          return (
            <div key={field.code}>
              <Text type="small" bold style={{ marginBottom: 4 }}>{field.label}</Text>
              <select
                value={fieldValues[field.code] ?? ''}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                style={{
                  width: '100%',
                  height: 44,
                  padding: '9px 12px',
                  fontSize: 18,
                  borderRadius: 8,
                  border: `1px solid ${colors.border5}`,
                  fontFamily: '"Red Hat Display", sans-serif',
                  backgroundColor: 'transparent',
                  outline: 'none',
                }}
              >
                <option value="">—</option>
                {field.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          );
        }

        return (
          <div key={field.code}>
            <Text type="small" bold style={{ marginBottom: 4 }}>{field.label}</Text>
            <input
              type={field.inputType === 'number' ? 'number' : 'text'}
              value={fieldValues[field.code] ?? ''}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              style={{
                width: '100%',
                height: 44,
                padding: '9px 12px',
                fontSize: 18,
                borderRadius: 8,
                border: `1px solid ${colors.border5}`,
                fontFamily: '"Red Hat Display", sans-serif',
                boxSizing: 'border-box',
                outline: 'none',
              }}
            />
          </div>
        );
      })}
    </Flex>
  );
};
